import { supabase } from '@/api/supabaseClient';

// Replaces integrations.Core.UploadFile. Six call sites store the returned URL
// straight into photo_url / avatar_url as a string, so the { file_url } shape
// is kept.
//
// Object paths are `{user_id}/{uuid}.{ext}`; the storage policies read that
// first segment to decide ownership, so it must stay the uploader's id.

const MAX_BYTES = 10 * 1024 * 1024;

function extensionFor(file) {
  const fromName = file.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
  if (fromName && /^[a-z0-9]{1,5}$/.test(fromName)) return fromName;
  const fromType = file.type?.split('/')[1];
  return fromType && /^[a-z0-9]{1,5}$/.test(fromType) ? fromType : 'bin';
}

export async function uploadFile({ file, bucket = 'photos' }) {
  if (!file) throw new Error('No file provided');
  if (file.size > MAX_BYTES) {
    throw new Error('That image is larger than 10MB. Please choose a smaller one.');
  }

  const { data: sessionData } = await supabase.auth.getUser();
  const userId = sessionData?.user?.id;
  if (!userId) throw new Error('You must be logged in to upload a file');

  const path = `${userId}/${crypto.randomUUID()}.${extensionFor(file)}`;

  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '31536000',
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return { file_url: data.publicUrl, path, bucket };
}

export async function uploadAvatar({ file }) {
  return uploadFile({ file, bucket: 'avatars' });
}

export const integrations = { Core: { UploadFile: uploadFile } };
export const storage = { uploadFile, uploadAvatar };
